import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const MAX_PER_ROOM = 6;

const FLOORS = [
  { key: 'G', label: 'Ground Floor' },
  { key: 'F', label: 'First Floor' },
  { key: 'S', label: 'Second Floor' },
  { key: 'T', label: 'Third Floor' },
];

// Color + label per status
const STATUS_STYLE = {
  Available: { bg: '#c6f6d5', border: '#9ae6b4', text: '#276749', dot: '#38a169' },
  Shared:    { bg: '#fefcbf', border: '#f6e05e', text: '#744210', dot: '#d69e2e' },
  Full:      { bg: '#fed7d7', border: '#fc8181', text: '#9b2c2c', dot: '#e53e3e' },
};

// Small bed-slot dots showing occupancy
function BedDots({ count, capacity }) {
  return (
    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 5, flexWrap: 'wrap' }}>
      {Array.from({ length: capacity }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: i < count ? '#2d3748' : 'rgba(0,0,0,0.15)',
          }}
        />
      ))}
    </div>
  );
}

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterFloor, setFilterFloor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  const load = () => {
    setLoading(true);
    api.get('/rooms').then(r => setRooms(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = rooms.filter(r => {
    if (filterFloor !== 'all' && r.room[0] !== filterFloor) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  const totalStudents = rooms.reduce((s, r) => s + r.count, 0);
  const totalSlots    = rooms.length * MAX_PER_ROOM;
  const fullRooms     = rooms.filter(r => r.status === 'Full').length;
  const sharedRooms   = rooms.filter(r => r.status === 'Shared').length;
  const availableRooms= rooms.filter(r => r.status === 'Available').length;
  const freeSlots     = rooms.reduce((s, r) => s + r.availableSlots, 0);

  return (
    <Layout title="Room Management">

      {/* ── Stats Row ─────────────────────────────────── */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
        {[
          { icon: '🏠', label: 'Total Rooms',    value: 60,             bg: '#3182ce' },
          { icon: '✅', label: 'Available',       value: availableRooms, bg: '#38a169' },
          { icon: '🟡', label: 'Shared (Partial)',value: sharedRooms,    bg: '#d69e2e' },
          { icon: '🔴', label: 'Full',            value: fullRooms,      bg: '#e53e3e' },
          { icon: '👤', label: 'Total Students',  value: totalStudents,  bg: '#6b46c1' },
          { icon: '🛏️', label: 'Free Beds',       value: freeSlots,      bg: '#0987a0' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ background: s.bg, color: '#fff' }}>{s.icon}</div>
            <div className="stat-info"><h3>{s.value}</h3><p>{s.label}</p></div>
          </div>
        ))}
      </div>

      {/* ── Capacity Bar ──────────────────────────────── */}
      <div className="card" style={{ padding: '14px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Overall Hostel Capacity</span>
          <span style={{ fontSize: 13, color: '#718096' }}>
            {totalStudents} / {totalSlots} beds used ({Math.round((totalStudents / totalSlots) * 100)}%)
          </span>
        </div>
        <div style={{ background: '#e2e8f0', borderRadius: 6, height: 12, overflow: 'hidden' }}>
          <div style={{
            width: `${(totalStudents / totalSlots) * 100}%`,
            background: 'linear-gradient(90deg, #38a169, #d69e2e, #e53e3e)',
            height: '100%', borderRadius: 6, transition: 'width 0.4s'
          }} />
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
          {Object.entries(STATUS_STYLE).map(([label, s]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.dot }} />
              <span style={{ fontSize: 11, color: '#718096' }}>{label}</span>
            </div>
          ))}
          <span style={{ fontSize: 11, color: '#718096', marginLeft: 'auto' }}>
            Max {MAX_PER_ROOM} students per room
          </span>
        </div>
      </div>

      {/* ── Main Card ─────────────────────────────────── */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">🏠 Room Grid</h3>
          <div className="flex gap-10" style={{ flexWrap: 'wrap' }}>
            {/* Floor filter */}
            <select value={filterFloor} onChange={e => setFilterFloor(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
              <option value="all">All Floors</option>
              {FLOORS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            {/* Status filter */}
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
              <option value="all">All Status</option>
              <option value="Available">Available</option>
              <option value="Shared">Shared</option>
              <option value="Full">Full</option>
            </select>
            {/* View toggle */}
            <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              {['grid', 'table'].map(m => (
                <button key={m} onClick={() => setViewMode(m)}
                  style={{
                    padding: '6px 12px', fontSize: 13, border: 'none', cursor: 'pointer',
                    background: viewMode === m ? '#3182ce' : '#fff',
                    color: viewMode === m ? '#fff' : '#4a5568',
                  }}>
                  {m === 'grid' ? '⊞ Grid' : '≡ Table'}
                </button>
              ))}
            </div>
            <button className="btn btn-outline btn-sm" onClick={load}>🔄 Refresh</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 50, color: '#718096' }}>Loading rooms...</div>
        ) : viewMode === 'grid' ? (

          /* ── GRID VIEW ─────────────────────────────── */
          <>
            {FLOORS.map(floor => {
              const floorRooms = filtered.filter(r => r.room[0] === floor.key);
              if (floorRooms.length === 0) return null;
              return (
                <div key={floor.key} style={{ marginBottom: 24 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                    paddingBottom: 8, borderBottom: '1px solid #f0f4f8'
                  }}>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', margin: 0 }}>
                      {floor.label}
                    </h4>
                    <span style={{ fontSize: 11, color: '#718096' }}>
                      ({floorRooms.filter(r => r.status !== 'Available').length} rooms in use,{' '}
                       {floorRooms.reduce((s, r) => s + r.availableSlots, 0)} free beds)
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(105px, 1fr))',
                    gap: 10
                  }}>
                    {floorRooms.map(r => {
                      const st = STATUS_STYLE[r.status];
                      return (
                        <div
                          key={r.room}
                          onClick={() => setSelected(r)}
                          style={{
                            background: st.bg,
                            border: `2px solid ${st.border}`,
                            borderRadius: 10,
                            padding: '10px 8px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'transform 0.15s, box-shadow 0.15s',
                            position: 'relative',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                          {/* Room number */}
                          <div style={{ fontSize: 15, fontWeight: 800, color: st.text }}>{r.room}</div>

                          {/* Occupancy fraction */}
                          <div style={{ fontSize: 11, color: st.text, marginTop: 2, fontWeight: 600 }}>
                            {r.count}/{r.capacity}
                          </div>

                          {/* Bed dots */}
                          <BedDots count={r.count} capacity={r.capacity} />

                          {/* Status label */}
                          <div style={{
                            fontSize: 10, fontWeight: 700, marginTop: 5, color: st.text,
                            textTransform: 'uppercase', letterSpacing: 0.4
                          }}>
                            {r.status === 'Available' ? 'Free' : r.status === 'Shared' ? `${r.availableSlots} left` : 'Full'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>

        ) : (

          /* ── TABLE VIEW ────────────────────────────── */
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Floor</th>
                  <th>Status</th>
                  <th>Occupancy</th>
                  <th>Free Beds</th>
                  <th>Students</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#718096' }}>No rooms match filter</td></tr>
                ) : filtered.map(r => (
                  <tr key={r.room} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                    <td><strong style={{ fontSize: 14 }}>{r.room}</strong></td>
                    <td style={{ fontSize: 12, color: '#718096' }}>{r.floor}</td>
                    <td>
                      <span style={{
                        padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: STATUS_STYLE[r.status].bg, color: STATUS_STYLE[r.status].text,
                        border: `1px solid ${STATUS_STYLE[r.status].border}`
                      }}>
                        {r.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ background: '#e2e8f0', borderRadius: 4, height: 8, width: 60, overflow: 'hidden' }}>
                          <div style={{
                            width: `${(r.count / r.capacity) * 100}%`,
                            background: r.status === 'Full' ? '#e53e3e' : r.status === 'Shared' ? '#d69e2e' : '#38a169',
                            height: '100%'
                          }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{r.count}/{r.capacity}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: 13, fontWeight: 600, color: r.availableSlots === 0 ? '#e53e3e' : '#38a169' }}>
                        {r.availableSlots}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.occupants.length === 0
                        ? <span style={{ color: '#a0aec0' }}>—</span>
                        : r.occupants.map(o => o.name).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Room Detail Modal ─────────────────────────── */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Room {selected.room} — {selected.floor}</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">

              {/* Occupancy visual */}
              <div style={{
                background: STATUS_STYLE[selected.status].bg,
                border: `1px solid ${STATUS_STYLE[selected.status].border}`,
                borderRadius: 10, padding: 16, textAlign: 'center', marginBottom: 16
              }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: STATUS_STYLE[selected.status].text }}>
                  {selected.count}/{selected.capacity}
                </div>
                <div style={{ fontSize: 12, color: STATUS_STYLE[selected.status].text, marginBottom: 8 }}>
                  beds occupied
                </div>
                <BedDots count={selected.count} capacity={selected.capacity} />
                <div style={{ marginTop: 8 }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                    background: '#fff', color: STATUS_STYLE[selected.status].text,
                    border: `1px solid ${STATUS_STYLE[selected.status].border}`
                  }}>
                    {selected.status} — {selected.availableSlots} bed{selected.availableSlots !== 1 ? 's' : ''} free
                  </span>
                </div>
              </div>

              {/* Occupants list */}
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#4a5568', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Current Occupants
                </p>
                {selected.occupants.length === 0 ? (
                  <p style={{ color: '#a0aec0', fontSize: 13 }}>No students assigned yet.</p>
                ) : (
                  selected.occupants.map((o, i) => (
                    <div key={o.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', background: '#f7fafc', borderRadius: 8, marginBottom: 6
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: '#3182ce', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0
                      }}>
                        {i + 1}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{o.name}</div>
                        {o.contact && <div style={{ fontSize: 11, color: '#718096' }}>{o.contact}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Empty bed slots */}
              {selected.availableSlots > 0 && (
                <div style={{ marginTop: 8 }}>
                  {Array.from({ length: selected.availableSlots }).map((_, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', border: '1px dashed #e2e8f0', borderRadius: 8, marginBottom: 6
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', background: '#f0f4f8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0
                      }}>🛏️</div>
                      <span style={{ fontSize: 12, color: '#a0aec0' }}>Available bed</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
